import { recordHook } from '@flatfile/plugin-record-hook'
import { format, isValid } from 'date-fns'
import {
  parseDateFromString,
  findCountry,
  findUSState,
  formatAndValidateCurrency,
} from 'utils'
import * as yup from 'yup'
import { US_COUNTRY_VALUE } from 'config/locations'
import { invalidPostalCodes } from 'config/products'
import { notificationServicesFields } from '../config/notificationsFields'
import { RECORDKEEPER_RULES } from '../config/recordkeeper-rules'

const numericRegex = /[0-9]/g
const flatten = (s) =>
  s
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')

export const recordkeeperFormattingListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const recordkeeper = record.get('recordkeeper')
      const flat = flatten(recordkeeper)
      for (const rule of RECORDKEEPER_RULES) {
        const matchTerms = rule.containsAny.map(flatten)
        if (matchTerms.some((match) => flat.includes(match))) {
          if (recordkeeper !== rule.target)
            record.set('recordkeeper', rule.target)
          return record
        }
      }
    })
  )
}

export const countryCodeFormattingListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const countryCode = record.get('countryCode')
      if (countryCode) {
        const matchingCountry = findCountry(countryCode)
        if (matchingCountry?.value === US_COUNTRY_VALUE) {
          record.set('countryCode', matchingCountry.value)
        } else {
          record.addError(
            'countryCode',
            'Must be a valid country name or country code'
          )
        }
      }
      return record
    })
  )
}

export const stateFormattingListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const state = record.get('state')
      if (state) {
        const matchingState = findUSState(state)
        if (matchingState) {
          record.set('state', matchingState.value)
        } else {
          record.addError('state', 'Must be a valid US state')
        }
      }
      return record
    })
  )
}

export const zipCodeValidationListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const zipCode = record.get('zipCode')
      if (zipCode) {
        const zipCodeRe = new RegExp('^[0-9]{5}(?:-[0-9]{4})?$')
        const postalCode = zipCode.substring(0, 5)
        if (!zipCodeRe.test(zipCode)) {
          record.addError(
            'zipCode',
            'US Zip Codes must have format: XXXXX or XXXXX-XXXX'
          )
        } else if (invalidPostalCodes.includes(postalCode)) {
          record.addError('zipCode', 'Please enter a valid Zip Code')
        }
      }
      return record
    })
  )
}

export const balanceVestedFormattingAndValidationListener = (
  listener,
  sheetSlug
) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const balanceVested = record.get(
        notificationServicesFields.BAL_VESTED.key
      )
      if (balanceVested) {
        formatAndValidateCurrency(
          balanceVested,
          notificationServicesFields.BAL_VESTED.key,
          record
        )
      }
      return record
    })
  )
}

export const balanceTotalFormattingAndValidationListener = (
  listener,
  sheetSlug
) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const balanceTotal = record.get(notificationServicesFields.BAL_TOTAL.key)
      if (balanceTotal) {
        formatAndValidateCurrency(
          balanceTotal,
          notificationServicesFields.BAL_TOTAL.key,
          record
        )
      }
      return record
    })
  )
}

export const dateOfBalanceFormattingListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const dateOfBalance = record.get(
        notificationServicesFields.DATE_OF_BALANCE.key
      )
      if (dateOfBalance) {
        const dateOfBalanceDate = parseDateFromString(dateOfBalance.trim())
        let formattedDateOfBalance = dateOfBalance
        if (dateOfBalanceDate) {
          formattedDateOfBalance = format(dateOfBalanceDate, 'yyyy/MM/dd')
          record.set(
            notificationServicesFields.DATE_OF_BALANCE.key,
            formattedDateOfBalance
          )
        } else {
          const sanitizedDateOfBalance = dateOfBalance
            .match(numericRegex)
            ?.join('')
          if (sanitizedDateOfBalance) {
            formattedDateOfBalance = sanitizedDateOfBalance.replace(
              /(\d{4})(\d{2})(\d{2})/,
              '$1/$2/$3'
            )
            record.set(
              notificationServicesFields.DATE_OF_BALANCE.key,
              formattedDateOfBalance
            )
          }
        }
      }
      return record
    })
  )
}

export const dateOfBalanceValidationListener = (listener, sheetSlug) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const dateOfBalance = record.get(
        notificationServicesFields.DATE_OF_BALANCE.key
      )
      if (dateOfBalance) {
        const dateOfBalanceDate = new Date(dateOfBalance)
        if (!isValid(dateOfBalanceDate)) {
          const dateRe = new RegExp('^[0-9]{4}/[0-9]{2}/[0-9]{2}$')
          dateRe.test(dateOfBalance)
            ? record.addError(
                notificationServicesFields.DATE_OF_BALANCE.key,
                'Must be a valid date'
              )
            : record.addError(
                notificationServicesFields.DATE_OF_BALANCE.key,
                'Format must be yyyy/mm/dd'
              )
        }
      }
      return record
    })
  )
}

export const reasonForRolloutRequestFormattingListener = (
  listener,
  sheetSlug
) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const reasonForRolloutRequest = record.get(
        notificationServicesFields.REASON_ROLLOUT_REQ.key
      )
      if (
        reasonForRolloutRequest &&
        (reasonForRolloutRequest.toLowerCase().trim() === 'sbr' ||
          reasonForRolloutRequest.toLowerCase().trim() === 'term')
      ) {
        record.set(
          notificationServicesFields.REASON_ROLLOUT_REQ.key,
          reasonForRolloutRequest.toUpperCase().trim()
        )
      }
      return record
    })
  )
}

export const reasonForRolloutRequestValidationListener = (
  listener,
  sheetSlug
) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const reasonForRolloutRequest = record.get(
        notificationServicesFields.REASON_ROLLOUT_REQ.key
      )
      const balanceVested = record.get(
        notificationServicesFields.BAL_VESTED.key
      )
      const balanceTotal = record.get(notificationServicesFields.BAL_TOTAL.key)
      if (reasonForRolloutRequest) {
        if (
          reasonForRolloutRequest.toLowerCase().trim() === 'sbr' &&
          !balanceVested
        ) {
          record.addError(
            notificationServicesFields.BAL_VESTED.key,
            'Required for small balance rollout (SBR).'
          )
        }
        if (
          reasonForRolloutRequest.toLowerCase().trim() === 'term' &&
          !balanceTotal
        ) {
          record.addError(
            notificationServicesFields.BAL_TOTAL.key,
            'Required for terminating plans (TERM).'
          )
        }
      }
      return record
    })
  )
}

export const gracePeriodForParticipantToRespondFormattingAndValidationListener =
  (listener, sheetSlug) => {
    listener.use(
      recordHook(sheetSlug, (record) => {
        const gracePeriod = record.get(
          notificationServicesFields.GRACE_PERIOD_TO_RESPOND.key
        )
        if (gracePeriod) {
          const sanitizedGracePeriod = gracePeriod.match(numericRegex)?.join('')
          if (!sanitizedGracePeriod || !/^\d+$/.test(sanitizedGracePeriod)) {
            record.addError(
              notificationServicesFields.GRACE_PERIOD_TO_RESPOND.key,
              'The amount of days the participant has to respond to the rollout notice and should account for mailing time'
            )
          } else {
            record.set(
              notificationServicesFields.GRACE_PERIOD_TO_RESPOND.key,
              sanitizedGracePeriod
            )
          }
        }
        return record
      })
    )
  }

export const rolloutHelpCompanyStateFormattingAndValidationListener = (
  listener,
  sheetSlug
) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const rolloutHelpCompanyState = record.get(
        notificationServicesFields.COMPANY_STATE.key
      )
      if (rolloutHelpCompanyState) {
        const matchingState = findUSState(rolloutHelpCompanyState)
        if (matchingState) {
          record.set(
            notificationServicesFields.COMPANY_STATE.key,
            matchingState.value
          )
        } else {
          record.addError(
            notificationServicesFields.COMPANY_STATE.key,
            'Must be a valid US state'
          )
        }
      }
      return record
    })
  )
}

export const rolloutHelpCompanyZipFormattingListener = (
  listener,
  sheetSlug
) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const rolloutHelpCompanyZip = record.get(
        notificationServicesFields.COMPANY_ZIP.key
      )
      if (rolloutHelpCompanyZip) {
        const sanitizedInput = rolloutHelpCompanyZip.replaceAll(
          /[^a-zA-Z0-9- ]/g,
          ''
        )
        let sanitizedZipCode = sanitizedInput.match(numericRegex)?.join('')
        if (sanitizedZipCode) {
          record.set(
            notificationServicesFields.COMPANY_ZIP.key,
            sanitizedZipCode
          )
          if (sanitizedZipCode.length === 4 || sanitizedZipCode.length === 8) {
            sanitizedZipCode = '0' + sanitizedZipCode
            record.set(
              notificationServicesFields.COMPANY_ZIP.key,
              sanitizedZipCode
            )
          } else if (
            sanitizedZipCode.length === 3 ||
            sanitizedZipCode.length === 7
          ) {
            sanitizedZipCode = '00' + sanitizedZipCode
            record.set(
              notificationServicesFields.COMPANY_ZIP.key,
              sanitizedZipCode
            )
          } else if (
            sanitizedZipCode.length === 2 ||
            sanitizedZipCode.length === 6
          ) {
            sanitizedZipCode = '000' + sanitizedZipCode
            record.set(
              notificationServicesFields.COMPANY_ZIP.key,
              sanitizedZipCode
            )
          }
        }
        if (sanitizedZipCode.length > 5) {
          record.set(
            notificationServicesFields.COMPANY_ZIP.key,
            sanitizedZipCode.slice(0, 5) + '-' + sanitizedZipCode.slice(5)
          )
        }
      }
      return record
    })
  )
}

export const rolloutHelpCompanyZipValidationListener = (
  listener,
  sheetSlug
) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const rolloutHelpCompanyZip = record.get(
        notificationServicesFields.COMPANY_ZIP.key
      )
      if (rolloutHelpCompanyZip) {
        const zipCodeRe = new RegExp('^[0-9]{5}(?:-[0-9]{4})?$')
        const postalCode = rolloutHelpCompanyZip.substring(0, 5)
        if (!zipCodeRe.test(rolloutHelpCompanyZip)) {
          record.addError(
            notificationServicesFields.COMPANY_ZIP.key,
            'US Zip Codes must have format: XXXXX or XXXXX-XXXX'
          )
        } else if (invalidPostalCodes.includes(postalCode)) {
          record.addError(
            notificationServicesFields.COMPANY_ZIP.key,
            'Please enter a valid Zip Code'
          )
        }
      }
      return record
    })
  )
}

export const rolloutHelpCompanyEmailValidationListener = (
  listener,
  sheetSlug
) => {
  listener.use(
    recordHook(sheetSlug, (record) => {
      const rolloutHelpCompanyEmail = record.get(
        notificationServicesFields.COMPANY_CONTACT_EMAIL.key
      )
      if (rolloutHelpCompanyEmail) {
        const schema = yup.string().email()
        const isValidEmail = schema.isValidSync(rolloutHelpCompanyEmail)
        if (!isValidEmail) {
          record.addError(
            notificationServicesFields.COMPANY_CONTACT_EMAIL.key,
            'Must be valid email format: example@email.com'
          )
        }
      }
      return record
    })
  )
}
